import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Users, Briefcase, Send, Mail, LogOut, Search, ExternalLink, FileText, Download, Link2 } from "lucide-react";
import { JobsManager } from "@/components/admin/JobsManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const STATUSES = ["New", "Contacted", "Shortlisted", "Rejected"];

const statusStyle = {
  New: "bg-blue-50 text-blue-700 border-blue-200",
  Contacted: "bg-amber-50 text-amber-700 border-amber-200",
  Shortlisted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const loadAll = async () => {
    try {
      const [s, e, m] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/enquiries", { params: { search, status: statusFilter } }),
        api.get("/admin/messages"),
      ]);
      setStats(s.data); setEnquiries(e.data); setMessages(m.data);
    } catch (err) {
      toast.error("Failed to load dashboard");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [search, statusFilter]);

  const updateStatus = async (id, next) => {
    try {
      const { data } = await api.patch(`/admin/enquiries/${id}/status`, { status: next });
      setEnquiries((prev) => prev.map((x) => (x.id === id ? { ...x, status: next } : x)));
      toast.success(data.emailed ? `Status updated to ${next} · applicant emailed` : `Status updated to ${next}`);
      loadAll();
    } catch { toast.error("Failed to update"); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/enquiries/export/csv", { params: { status: statusFilter }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `enquiries_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const kpis = [
    { label: "Total Enquiries", value: stats?.total_enquiries ?? "—", Icon: Users, tone: "indigo" },
    { label: "New Enquiries", value: stats?.new_enquiries ?? "—", Icon: Send, tone: "blue" },
    { label: "Applications", value: (stats?.contacted ?? 0) + (stats?.shortlisted ?? 0), Icon: Briefcase, tone: "emerald" },
    { label: "Jobs Posted", value: stats?.jobs_posted ?? "—", Icon: Briefcase, tone: "sky" },
  ];

  const toneMap = {
    indigo: "bg-indigo-50 text-indigo-700",
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Admin Dashboard</p>
          <h1 className="mt-2 text-3xl lg:text-4xl font-extrabold text-slate-900 font-display">Welcome, {user?.name || "Admin"}</h1>
          <p className="mt-1 text-slate-500 text-sm">Review job enquiries and manage recruitment pipeline.</p>
        </div>
        <Button data-testid="admin-logout" variant="outline" className="rounded-full self-start" onClick={logout}>
          <LogOut size={15} className="mr-2" /> Logout
        </Button>
      </div>

      {/* KPI grid */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-start justify-between">
              <span className={`inline-flex w-10 h-10 rounded-xl ${toneMap[k.tone]} items-center justify-center`}><k.Icon size={18} /></span>
            </div>
            <p className="mt-4 text-3xl font-extrabold text-slate-900 font-display">{k.value}</p>
            <p className="text-xs text-slate-500 mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="enquiries" className="mt-8">
        <TabsList data-testid="admin-tabs" className="bg-slate-100 rounded-full p-1">
          <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5">Enquiries</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5">Jobs</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5">
            Messages {stats?.unread_messages ? <Badge className="ml-2 bg-rose-500 text-white">{stats.unread_messages}</Badge> : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="mt-6">
          <JobsManager onChanged={loadAll} />
        </TabsContent>

        <TabsContent value="enquiries" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="p-4 lg:p-5 flex flex-col md:flex-row gap-3 border-b border-slate-100">
              <div className="flex-1 relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="admin-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, or role…" className="pl-9 h-10" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="admin-status-filter" className="h-10 md:w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button data-testid="export-csv-button" variant="outline" className="h-10 rounded-full" onClick={exportCsv}>
                <Download size={15} className="mr-2" /> Export CSV
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table data-testid="admin-enquiry-table" className="min-w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr className="text-left text-xs font-semibold uppercase tracking-widest text-slate-500">
                    <th className="px-5 py-3">Applicant</th>
                    <th className="px-5 py-3">Contact</th>
                    <th className="px-5 py-3">Preferred Job</th>
                    <th className="px-5 py-3">Location</th>
                    <th className="px-5 py-3">Experience</th>
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-12 text-slate-500">No enquiries yet.</td></tr>
                  ) : enquiries.map((e) => (
                    <tr key={e.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-5 py-4">
                        <p className="font-semibold text-slate-900">{e.full_name}</p>
                        <p className="text-xs text-slate-500">{e.qualification}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-slate-700">{e.email}</p>
                        <p className="text-xs text-slate-500">{e.mobile}</p>
                      </td>
                      <td className="px-5 py-4 text-slate-700">{e.preferred_role}</td>
                      <td className="px-5 py-4 text-slate-700">{e.preferred_location}</td>
                      <td className="px-5 py-4 text-slate-700">{e.experience}</td>
                      <td className="px-5 py-4 text-slate-500 text-xs">{new Date(e.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-4">
                        <Select value={e.status} onValueChange={(v) => updateStatus(e.id, v)}>
                          <SelectTrigger data-testid={`status-select-${e.id}`} className={`h-9 w-36 border ${statusStyle[e.status] || "border-slate-200"}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <Button data-testid={`view-enquiry-${e.id}`} size="sm" variant="outline" className="rounded-full" onClick={() => setSelected(e)}>View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="messages" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
            {messages.length === 0 ? (
              <p className="text-center text-slate-500 py-12">No messages yet.</p>
            ) : messages.map((m) => (
              <div key={m.id} data-testid={`message-${m.id}`} className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-slate-900">{m.name} <span className="text-xs text-slate-500 font-normal ml-1">· {m.email}</span></p>
                    <p className="text-sm text-slate-600 mt-0.5">{m.subject}</p>
                  </div>
                  <p className="text-xs text-slate-400 whitespace-nowrap">{new Date(m.created_at).toLocaleString()}</p>
                </div>
                <p className="mt-3 text-sm text-slate-700 leading-relaxed">{m.message}</p>
                <p className="mt-2 text-xs text-slate-500 flex items-center gap-1.5"><Mail size={12} /> {m.phone}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="enquiry-detail-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{selected.full_name}</DialogTitle>
                <p className="text-sm text-slate-500">{selected.preferred_role} · {selected.preferred_location}</p>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-4 mt-4 text-sm">
                <Detail label="Email" value={selected.email} />
                <Detail label="Mobile" value={selected.mobile} />
                <Detail label="City / State" value={`${selected.city}, ${selected.state}`} />
                <Detail label="Qualification" value={selected.qualification} />
                <Detail label="Experience" value={selected.experience} />
                <Detail label="Current Title" value={selected.current_title || "—"} />
                <Detail label="Expected Salary" value={selected.expected_salary} />
                <Detail label="Employment Type" value={selected.employment_type} />
                <Detail label="Skills" value={selected.skills} full />
                {selected.message && <Detail label="Message" value={selected.message} full />}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={`${BACKEND_URL}/api/files/${selected.resume_path}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="view-resume-link"
                >
                  <Button className="rounded-full bg-gradient-to-r from-blue-700 to-indigo-600 text-white">
                    <FileText size={15} className="mr-2" /> View Resume <ExternalLink size={13} className="ml-2" />
                  </Button>
                </a>
                <p className="text-xs text-slate-500 self-center">{selected.resume_filename}</p>
              </div>
              {selected.track_token && (
                <button
                  type="button"
                  data-testid="copy-applicant-track-link"
                  className="mt-3 text-xs text-indigo-700 inline-flex items-center gap-1.5 hover:underline"
                  onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/track/${selected.track_token}`); toast.success("Tracking link copied"); }}
                >
                  <Link2 size={12} /> Copy applicant tracking link
                </button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
      <p className="mt-1 text-slate-900">{value}</p>
    </div>
  );
}
